import {useState, useEffect, useCallback} from 'react';

interface UseLocalStorageTimerProps {
  timeLimitHours: number;
  storageKey: string;
  skipTimer: boolean;
  showOnFirstVisit: boolean;
}

function useLocalStorageTimer({
  timeLimitHours,
  storageKey,
  skipTimer,
  showOnFirstVisit = true
}: UseLocalStorageTimerProps) {
  const [timerExpired, setTimerExpired] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  // converts ms to hours for passed in value
  const hoursInMs = timeLimitHours * 60 * 60 * 1000;
  useEffect(() => {
    if (skipTimer) {
      if (window.localStorage.getItem(storageKey)) {
        window.localStorage.removeItem(storageKey);
      }
      return;
    }

    const storedTime = window.localStorage.getItem(storageKey);
    // oxlint-disable-next-line react/react-compiler -- reads localStorage (external system) then publishes timer state
    setIsFirstVisit(!storedTime);

    // Check if timer was stored previously and is expired
    if (storedTime && Date.now() > parseInt(storedTime, 10)) {
      setTimerExpired(true);
      return;
    }
    if (!storedTime) {
      // Calculate expiry and set in localStorage
      const newExpiryTime = Date.now() + hoursInMs;
      window.localStorage.setItem(storageKey, newExpiryTime.toString());
    }
  }, [timeLimitHours, hoursInMs, storageKey, skipTimer, showOnFirstVisit]);

  // Extend the current storage expiration
  const resetTimer = () => {
    const newExpiryTime = Date.now() + hoursInMs;
    window.localStorage.setItem(storageKey, newExpiryTime.toString());
    setIsFirstVisit(false);
    setTimerExpired(false);
  };

  // Clear the current storage expiration
  const clearTimer = useCallback(() => {
    window.localStorage.removeItem(storageKey);
    setTimerExpired(false);
  }, [storageKey]);

  const hasExpired = timerExpired || isFirstVisit;

  return {
    hasExpired,
    resetTimer,
    clearTimer
  };
}

export default useLocalStorageTimer;
